/**
 * GSAP Animation System with ScrollTrigger
 * 
 * Advanced animation system using GSAP and ScrollTrigger for smooth,
 * performant animations with scroll-based triggers.
 * 
 * @package Alliant
 * @since 2.0.0
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

class GSAPAnimationSystem {
    constructor() {
        this.animations = new Map();
        this.init();
    }

    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupAnimations());
        } else {
            this.setupAnimations();
        }
    }

    setupAnimations() {
        // Set up default animations
        this.setupFadeInAnimations();
        this.setupSlideInAnimations();
        this.setupStaggerAnimations();
        this.setupSlowStaggerAnimations();
        this.setupScaleAnimations();
        
        // Refresh ScrollTrigger to ensure all animations are registered
        ScrollTrigger.refresh();
        
        console.log('[GSAP] Animation system initialized with ScrollTrigger');
    }

    /**
     * Check if element is inside a carousel slide
     * Elements in carousels should be handled by the carousel system, not ScrollTrigger
     */
    isInCarousel(element) {
        return element.closest('.carousel-slide') !== null;
    }

    /**
     * Fade in animations
     */
    setupFadeInAnimations() {
        const fadeElements = document.querySelectorAll('.gsap-fade-in');
        
        fadeElements.forEach(element => {
            // Skip elements inside carousel slides - they'll be handled by the carousel system
            if (this.isInCarousel(element)) {
                return;
            }
            
            // Check for delay classes
            const delay = this.getDelayFromClasses(element);
            
            const animation = gsap.fromTo(element, 
                { 
                    opacity: 0,
                    y: -50
                },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    delay: delay,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: element,
                        start: "top 80%",
                        toggleActions: "play none none none"
                    }
                }
            );
            
            this.animations.set(element, animation);
        });
    }

    /**
     * Slide in animations from different directions
     */
    setupSlideInAnimations() {
        // Slide from left
        const slideLeftElements = document.querySelectorAll('.gsap-slide-left');
        slideLeftElements.forEach(element => {
            // Skip elements inside carousel slides
            if (this.isInCarousel(element)) {
                return;
            }
            
            const delay = this.getDelayFromClasses(element);
            const animation = gsap.fromTo(element,
                { 
                    x: -100,
                    opacity: 0
                },
                {
                    x: 0,
                    opacity: 1,
                    duration: 1,
                    delay: delay,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: element,
                        start: "top 80%",
                        toggleActions: "play none none none"
                    }
                }
            );
            this.animations.set(element, animation);
        });

        // Slide from right
        const slideRightElements = document.querySelectorAll('.gsap-slide-right');
        slideRightElements.forEach(element => {
            // Skip elements inside carousel slides
            if (this.isInCarousel(element)) {
                return;
            }
            
            const delay = this.getDelayFromClasses(element);
            const animation = gsap.fromTo(element,
                { 
                    x: 100,
                    opacity: 0
                },
                {
                    x: 0,
                    opacity: 1,
                    duration: 1,
                    delay: delay,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: element,
                        start: "top 80%",
                        toggleActions: "play none none none"
                    }
                }
            );
            this.animations.set(element, animation);
        });

        // Slide from bottom
        const slideUpElements = document.querySelectorAll('.gsap-slide-up');
        slideUpElements.forEach(element => {
            // Skip elements inside carousel slides
            if (this.isInCarousel(element)) {
                return;
            }
            
            const delay = this.getDelayFromClasses(element);
            const animation = gsap.fromTo(element,
                { 
                    y: 100,
                    opacity: 0
                },
                {
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    delay: delay,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: element,
                        start: "top 80%",
                        toggleActions: "play none none none"
                    }
                }
            );
            this.animations.set(element, animation);
        });

        // Slide from top
        const slideDownElements = document.querySelectorAll('.gsap-slide-down');
        slideDownElements.forEach(element => {
            // Skip elements inside carousel slides
            if (this.isInCarousel(element)) {
                return;
            }
            
            const delay = this.getDelayFromClasses(element);
            const animation = gsap.fromTo(element,
                { 
                    y: -100,
                    opacity: 0
                },
                {
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    delay: delay,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: element,
                        start: "top 80%",
                        toggleActions: "play none none none"
                    }
                }
            );
            this.animations.set(element, animation);
        });
    }

    /**
     * Stagger animations for lists and grids
     */
    setupStaggerAnimations() {
        const staggerElements = document.querySelectorAll('.gsap-stagger');
        
        staggerElements.forEach(element => {
            // Skip elements inside carousel slides
            if (this.isInCarousel(element)) {
                return;
            }
            
            const children = element.children;
            
            if (children.length > 0) {
                const animation = gsap.fromTo(children,
                    { 
                        opacity: 0,
                        y: -50
                    },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        stagger: 0.1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: element,
                            start: "top 80%",
                            toggleActions: "play none none none"
                        }
                    }
                );
                
                this.animations.set(element, animation);
            }
        });
    }

    /**
     * Slow stagger animations for lists and grids
     */
    setupSlowStaggerAnimations() {
        const slowStaggerElements = document.querySelectorAll('.gsap-stagger-slow');
        
        slowStaggerElements.forEach(element => {
            // Skip elements inside carousel slides
            if (this.isInCarousel(element)) {
                return;
            }
            
            const children = element.children;
            
            if (children.length > 0) {
                const animation = gsap.fromTo(children,
                    { 
                        opacity: 0,
                        y: -50
                    },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1.2,
                        stagger: 0.3,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: element,
                            start: "top 80%",
                            toggleActions: "play none none none"
                        }
                    }
                );
                
                this.animations.set(element, animation);
            }
        });
    }

    /**
     * Scale animations
     */
    setupScaleAnimations() {
        const scaleElements = document.querySelectorAll('.gsap-scale');
        
        scaleElements.forEach(element => {
            // Skip elements inside carousel slides
            if (this.isInCarousel(element)) {
                return;
            }
            
            const delay = this.getDelayFromClasses(element);
            const animation = gsap.fromTo(element,
                { 
                    scale: 0.8,
                    opacity: 0
                },
                {
                    scale: 1,
                    opacity: 1,
                    duration: 1,
                    delay: delay,
                    ease: "back.out(1.7)",
                    scrollTrigger: {
                        trigger: element,
                        start: "top 80%",
                        toggleActions: "play none none none"
                    }
                }
            );
            
            this.animations.set(element, animation);
        });
    }

    /**
     * Create custom animation
     */
    createCustomAnimation(element, fromProps, toProps, scrollTriggerOptions = {}) {
        const defaultScrollTrigger = {
            trigger: element,
            start: "top 80%",
            toggleActions: "play none none reverse"
        };

        const animation = gsap.fromTo(element, fromProps, {
            ...toProps,
            scrollTrigger: {
                ...defaultScrollTrigger,
                ...scrollTriggerOptions
            }
        });

        this.animations.set(element, animation);
        return animation;
    }

    /**
     * Refresh all animations (useful for dynamic content)
     */
    refresh() {
        ScrollTrigger.refresh();
        console.log('[GSAP] Animations refreshed');
    }

    /**
     * Kill all animations
     */
    killAll() {
        this.animations.forEach(animation => {
            animation.kill();
        });
        this.animations.clear();
        ScrollTrigger.killAll();
    }

    /**
     * Get animation for specific element
     */
    getAnimation(element) {
        return this.animations.get(element);
    }

    /**
     * Get delay value from CSS classes
     */
    getDelayFromClasses(element) {
        const classList = element.classList;
        
        // Check for delay classes (in seconds)
        if (classList.contains('gsap-delay-0')) return 0;
        if (classList.contains('gsap-delay-100')) return 0.1;
        if (classList.contains('gsap-delay-200')) return 0.2;
        if (classList.contains('gsap-delay-300')) return 0.3;
        if (classList.contains('gsap-delay-400')) return 0.4;
        if (classList.contains('gsap-delay-500')) return 0.5;
        if (classList.contains('gsap-delay-600')) return 0.6;
        if (classList.contains('gsap-delay-700')) return 0.7;
        if (classList.contains('gsap-delay-800')) return 0.8;
        if (classList.contains('gsap-delay-900')) return 0.9;
        if (classList.contains('gsap-delay-1000')) return 1.0;
        if (classList.contains('gsap-delay-1200')) return 1.2;
        if (classList.contains('gsap-delay-1500')) return 1.5;
        if (classList.contains('gsap-delay-2000')) return 2.0;
        if (classList.contains('gsap-delay-2500')) return 2.5;
        if (classList.contains('gsap-delay-3000')) return 3.0;
        
        return 0; // No delay
    }
}

// Export the class
export default GSAPAnimationSystem;
